import React, { useMemo, useState } from "react";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PlusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { MinusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { type Product } from "@/types/types";
import {
  allCompetitors,
  fashionCompetitors,
  beautyCompetitors,
  electricalsCompetitors,
  type Competitor,
} from "./data/competitors";
import {
  FASHION_CHILDREN_2_PLUS_CATEGORY_IDS,
  FASHION_BRAS_CATEGORY_IDS,
  FASHION_CHILDRENSWEAR_CATEGORY_IDS,
  FASHION_DEFAULT_CATEGORY_IDS,
  ELECTRICALS_DEFAULT_CATEGORY_IDS,
  ELECTRICALS_ADDITIONAL_CATEGORY_IDS,
  BEAUTY_DEFAULT_CATEGORY_IDS,
} from "@/utils/category/categoryIds";
import { productIsInCategory } from "@/utils/category/productIsInCategory";
import styles from "./CompetitorList.module.scss";

interface CompetitorListProps {
  product: Product;
}

export const filterCompetitors = (
  product: Product,
  competitors: Competitor[],
) => {
  // Exclude Amazon for non-electrical products
  if (!productIsInCategory(product, ELECTRICALS_DEFAULT_CATEGORY_IDS)) {
    return competitors.filter((competitor) => competitor.key !== "amazon");
  }
  return competitors;
};

export const sortCompetitors = (
  product: Product,
  competitors: Competitor[],
) => {
  if (
    productIsInCategory(product, [
      ...FASHION_DEFAULT_CATEGORY_IDS,
      ...FASHION_CHILDRENSWEAR_CATEGORY_IDS,
      ...FASHION_CHILDREN_2_PLUS_CATEGORY_IDS,
      ...FASHION_BRAS_CATEGORY_IDS,
    ])
  ) {
    return prioritiseCompetitors(fashionCompetitors, competitors);
  }

  if (
    productIsInCategory(product, [
      ...ELECTRICALS_DEFAULT_CATEGORY_IDS,
      ...ELECTRICALS_ADDITIONAL_CATEGORY_IDS,
    ])
  ) {
    return prioritiseCompetitors(electricalsCompetitors, competitors);
  }

  if (productIsInCategory(product, BEAUTY_DEFAULT_CATEGORY_IDS)) {
    return prioritiseCompetitors(beautyCompetitors, competitors);
  }

  return competitors;
};

export const prioritiseCompetitors = (
  priorityList: string[],
  competitors: Competitor[],
) => {
  // Prioritised list must appear in the order defined in priorityList, hence the extra faff here
  const prioritised = priorityList
    .map((key) => competitors.find((competitor) => competitor.key === key))
    .filter((competitor): competitor is (typeof competitors)[number] =>
      Boolean(competitor),
    );
  const nonPrioritised = competitors.filter(
    (competitor) => !priorityList.includes(competitor.key),
  );
  return [...prioritised, ...nonPrioritised];
};

export const INITIAL_COMPETITOR_COUNT = 3;

export const CompetitorList: React.FC<CompetitorListProps> = ({ product }) => {
  const [showAllCompetitors, setShowAllCompetitors] = useState(false);

  const filteredCompetitors = useMemo(
    () => filterCompetitors(product, allCompetitors),
    [product],
  );
  const sortedCompetitors = useMemo(
    () => sortCompetitors(product, filteredCompetitors),
    [product, filteredCompetitors],
  );
  const displayedCompetitors = showAllCompetitors
    ? sortedCompetitors
    : sortedCompetitors.slice(0, INITIAL_COMPETITOR_COUNT);

  const hasMoreCompetitors =
    sortedCompetitors.length > INITIAL_COMPETITOR_COUNT;

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.heading}>We price match with:</h3>
      <ul
        className={`${styles.competitorList} ${
          showAllCompetitors ? styles.competitorListExpanded : ""
        }`}
      >
        {displayedCompetitors.map((competitor: Competitor, index: number) => {
          const isLastRow =
            Math.floor(index / INITIAL_COMPETITOR_COUNT) ===
            Math.floor(
              (displayedCompetitors.length - 1) / INITIAL_COMPETITOR_COUNT,
            );
          return (
            <li
              className={isLastRow ? styles.lastRow : ""}
              key={competitor.key}
            >
              <span className={styles.competitorName}>{competitor.name}</span>
            </li>
          );
        })}
      </ul>
      {hasMoreCompetitors && (
        <TertiaryButton
          onClick={() => setShowAllCompetitors(!showAllCompetitors)}
          className={styles.showMoreButton}
          data-testid="competitor-list-toggle"
          icon={showAllCompetitors ? MinusOutlined : PlusOutlined}
          labelText={showAllCompetitors ? `See less` : `See full list`}
        />
      )}
      <p className={styles.amazonDisclaimer}>
        For Amazon we match electricals only: tech, TV and audio, computing and
        gaming, small and large home appliances, mobile/smart tech and
        beauty/dental electricals.
      </p>
      <p className={styles.asosDisclaimer}>ASOS we don&apos;t match Beauty.</p>
      <p className={styles.appleDisclaimer}>
        When price matching Apple products, we will only match those sold by
        Apple directly. We will also price match Apple products sold by one of
        our named competitors if they offer a minimum 2 year guarantee as
        standard at no additional cost.
      </p>
    </div>
  );
};
